import React, { ReactNode } from 'react';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  Buildings,
  ClockCounterClockwise,
  IdentificationBadge,
  Robot,
  ShieldCheck,
  Stack,
  TreeStructure,
  UserCircle,
  Users,
  UsersThree,
} from 'phosphor-react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import ClientesForm from '../../components/forms/ClientesForm';
import UsersForm from '../../components/forms/UsersForm';
import { IUser, useUsers } from '../../hooks/useUsers';
import { IDataNoPagination } from '../../types/shared';
import { ReturnButton } from '../../components/buttons/ReturnButton';
import { useAuth } from '../../providers/auth';
import { ClientesCentral } from './ClientesCentral';
import { DepartamentosCentral } from './DepartamentosCentral';
import { FundoCentral } from './FundoCentral';
import { LogsDeAcesso } from './LogsDeAcesso';
import { McpPage } from '../mcp';
import { WhitelistCentral } from './WhitelistCentral';
import { Cartao, AZUL, AZUL_ESCURO, Eyebrow, TEXTO } from './ui';

export type AbaDaCentral =
  | 'grupos'
  | 'usuarios'
  | 'departamentos'
  | 'empresas'
  | 'catalogo'
  | 'clientes'
  | 'ip-whitelist'
  | 'mcp'
  | 'logs';

const CENTRAL = '/configuracoes/permissoes';

const SECOES: {
  titulo: string;
  /** `tela`: rota cujo acesso libera a aba (mesma regra do menu). */
  abas: { aba: AbaDaCentral; rotulo: string; icone: ReactNode; destino: string; tela: string }[];
}[] = [
  {
    titulo: 'PERMISSÕES',
    abas: [
      {
        aba: 'grupos',
        rotulo: 'Grupos',
        icone: <UsersThree size={16} />,
        destino: CENTRAL,
        tela: CENTRAL,
      },
      {
        aba: 'usuarios',
        rotulo: 'Usuários',
        icone: <UserCircle size={16} />,
        destino: `${CENTRAL}?modo=usuarios`,
        tela: CENTRAL,
      },
      {
        aba: 'departamentos',
        rotulo: 'Departamentos',
        icone: <TreeStructure size={16} />,
        destino: '/configuracoes/departamentos',
        tela: '/configuracoes/departamentos',
      },
      {
        aba: 'empresas',
        rotulo: 'Empresas',
        icone: <Buildings size={16} />,
        destino: `${CENTRAL}?modo=empresas`,
        tela: CENTRAL,
      },
      {
        aba: 'catalogo',
        rotulo: 'Catálogo',
        icone: <Stack size={16} />,
        destino: `${CENTRAL}?modo=catalogo`,
        tela: CENTRAL,
      },
    ],
  },
  {
    titulo: 'ACESSO',
    abas: [
      {
        aba: 'clientes',
        rotulo: 'Clientes',
        icone: <IdentificationBadge size={16} />,
        destino: '/configuracoes/clientes',
        tela: '/configuracoes/clientes',
      },
      {
        aba: 'ip-whitelist',
        rotulo: 'Whitelist de IPs',
        icone: <ShieldCheck size={16} />,
        destino: '/configuracoes/ip-whitelist',
        tela: '/configuracoes/ip-whitelist',
      },
      {
        aba: 'mcp',
        rotulo: 'MCP',
        icone: <Robot size={16} />,
        destino: '/configuracoes/mcp',
        tela: '/configuracoes/mcp',
      },
      {
        aba: 'logs',
        rotulo: 'Logs de acesso',
        icone: <ClockCounterClockwise size={16} />,
        destino: '/configuracoes/logs-de-acesso',
        tela: '/configuracoes/logs-de-acesso',
      },
    ],
  },
];

/** Quem abre a Central vê Clientes, Whitelist e Logs como abas dela (e não no menu). */
export function usePodeVerCentral() {
  const { isAdmin, hasPermission } = useAuth();
  return isAdmin() || hasPermission(CENTRAL);
}

function AbasDaCentral({ atual }: { atual: AbaDaCentral }) {
  const navigate = useNavigate();
  const { isAdmin, hasPermission } = useAuth();
  const secoes = SECOES.map(s => ({
    ...s,
    abas: s.abas.filter(a => isAdmin() || hasPermission(a.tela)),
  })).filter(s => s.abas.length > 0);

  return (
    <Box
      as="nav"
      aria-label="Seções da Central de Permissões"
      overflowX="auto"
      mx={{ base: '-16px', md: 0 }}
      px={{ base: '16px', md: 0 }}
    >
      <Flex
        display="inline-flex"
        align="center"
        gap="4px"
        p="5px"
        borderRadius="999px"
        bg="rgba(255,255,255,.7)"
        boxShadow="inset 0 0 0 1px rgba(15,23,42,.06), 0 8px 24px -12px rgba(15,23,42,.18)"
        backdropFilter="blur(12px)"
      >
        {secoes.map((s, i) => (
          <Flex key={s.titulo} align="center" gap="4px" flex="none">
            {i > 0 && <Box w="1px" h="22px" bg="rgba(15,23,42,.1)" mx="6px" />}
            {s.abas.map(a => {
              const ativa = a.aba === atual;
              return (
                <Flex
                  key={a.aba}
                  as="button"
                  type="button"
                  aria-current={ativa ? 'page' : undefined}
                  align="center"
                  gap="7px"
                  h="36px"
                  px={{ base: '12px', '2xl': '14px' }}
                  borderRadius="999px"
                  fontSize="13.5px"
                  fontWeight={600}
                  whiteSpace="nowrap"
                  color={ativa ? '#FFFFFF' : '#475569'}
                  bg={ativa ? `linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})` : 'transparent'}
                  boxShadow={ativa ? '0 6px 16px -4px rgba(0,109,158,.45)' : undefined}
                  _hover={ativa ? undefined : { bg: 'rgba(15,23,42,.05)', color: TEXTO }}
                  onClick={() => !ativa && navigate(a.destino)}
                >
                  {a.icone}
                  {a.rotulo}
                </Flex>
              );
            })}
          </Flex>
        ))}
      </Flex>
    </Box>
  );
}

/** Título da Central e a barra com todas as seções dela. */
export function CabecalhoCentral({ atual }: { atual: AbaDaCentral }) {
  return (
    <Flex direction="column" gap="18px">
      <Flex gap="14px" align="center">
        <Flex
          w="46px"
          h="46px"
          borderRadius="14px"
          align="center"
          justify="center"
          bg="#DFF3FF"
          color={AZUL}
          flex="none"
        >
          <Users size={24} weight="duotone" />
        </Flex>
        <Box>
          <Eyebrow>CONFIGURAÇÕES</Eyebrow>
          <Text
            fontSize="28px"
            fontWeight={700}
            letterSpacing="-.5px"
            color={TEXTO}
            lineHeight="34px"
          >
            Central de Permissões
          </Text>
          <Text fontSize="14px" color="#475569" maxW="720px">
            Quem pode ver e fazer o quê, em uma tela só: grupos, usuários, departamentos, empresas,
            catálogo de telas, clientes da Área VIP e IPs liberados.
          </Text>
        </Box>
      </Flex>
      <AbasDaCentral atual={atual} />
    </Flex>
  );
}

const TITULO: Record<AbaDaCentral, string> = {
  grupos: 'Grupos',
  usuarios: 'Usuários',
  departamentos: 'Departamentos',
  empresas: 'Empresas',
  catalogo: 'Catálogo',
  clientes: 'Clientes',
  'ip-whitelist': 'Whitelist de IPs',
  mcp: 'MCP',
  logs: 'Logs de acesso',
};

/**
 * Aba da Central com rota própria (rota `isGlass`): quem abre a Central vê a barra de seções;
 * quem só tem acesso a esta tela (ex.: Financeiro em Clientes) vê só o título dela.
 */
function NaCentral({ atual, children }: { atual: AbaDaCentral; children: ReactNode }) {
  const pode = usePodeVerCentral();
  return (
    <FundoCentral>
      {pode ? (
        <CabecalhoCentral atual={atual} />
      ) : (
        <Box>
          <Eyebrow>CONFIGURAÇÕES</Eyebrow>
          <Text
            fontSize="28px"
            fontWeight={700}
            letterSpacing="-.5px"
            color={TEXTO}
            lineHeight="34px"
          >
            {TITULO[atual]}
          </Text>
        </Box>
      )}
      {children}
    </FundoCentral>
  );
}

export const ClientesNaCentral = () => (
  <NaCentral atual="clientes">
    <ClientesCentral />
  </NaCentral>
);

/** Criar/editar cliente: o formulário de sempre, na moldura da Central. */
function FormularioDeCliente({ children }: { children: ReactNode }) {
  return (
    <NaCentral atual="clientes">
      <Box>
        <ReturnButton previousPage="configuracoes/clientes" label="Voltar para Clientes" />
      </Box>
      <Cartao p={{ base: '16px', md: '24px' }}>{children}</Cartao>
    </NaCentral>
  );
}

export const ClienteNovoNaCentral = () => (
  <FormularioDeCliente>
    <ClientesForm />
  </FormularioDeCliente>
);

export { FormularioDeCliente };

export const IpWhitelistNaCentral = () => (
  <NaCentral atual="ip-whitelist">
    <WhitelistCentral />
  </NaCentral>
);

/** Criar/editar IP agora é o painel da própria aba. */
export const IpNovoRedireciona = () => (
  <Navigate to="/configuracoes/ip-whitelist?ip=novo" replace />
);
export function IpEditarRedireciona() {
  const { itemId } = useParams();
  return <Navigate to={`/configuracoes/ip-whitelist?ip=${itemId}`} replace />;
}

/** MCP do sistema: ferramentas, chaves, sessões e atividade (só administrador). */
export const McpNaCentral = () => (
  <NaCentral atual="mcp">
    <McpPage />
  </NaCentral>
);

export const LogsNaCentral = () => (
  <NaCentral atual="logs">
    <LogsDeAcesso />
  </NaCentral>
);

export const DepartamentosNaCentral = () => (
  <NaCentral atual="departamentos">
    <DepartamentosCentral />
  </NaCentral>
);

/** Criar/editar departamento agora é o painel da própria aba. */
export const DepartamentoNovoRedireciona = () => (
  <Navigate to="/configuracoes/departamentos?dep=novo" replace />
);
export function DepartamentoEditarRedireciona() {
  const { itemId } = useParams();
  return <Navigate to={`/configuracoes/departamentos?dep=${itemId}`} replace />;
}

/**
 * A antiga lista de usuários virou a aba Usuários da Central (lá ficam ativar, campos extras e
 * acesso à conta). `usuario` na URL abre a pessoa.
 */
export function UsuariosRedireciona() {
  const { search } = useLocation();
  const usuario = new URLSearchParams(search).get('usuario');
  return (
    <Navigate to={usuario ? `${CENTRAL}?usuario=${usuario}` : `${CENTRAL}?modo=usuarios`} replace />
  );
}

/** Criar/editar usuário: o formulário de sempre, na moldura da Central. */
function FormularioDeUsuario({
  children,
  voltarPara,
}: {
  children: ReactNode;
  voltarPara: string;
}) {
  return (
    <NaCentral atual="usuarios">
      <Box>
        <ReturnButton previousPage={voltarPara} label="Voltar para Usuários" />
      </Box>
      <Cartao p={{ base: '16px', md: '24px' }}>{children}</Cartao>
    </NaCentral>
  );
}

export const UsuarioNovoNaCentral = () => (
  <FormularioDeUsuario voltarPara="configuracoes/permissoes?modo=usuarios">
    <UsersForm />
  </FormularioDeUsuario>
);

export function UsuarioEditarNaCentral() {
  const { getUser } = useUsers();
  const { itemId } = useParams();
  const { data, isFetching, isError } = useQuery<IDataNoPagination<IUser>>({
    queryFn: getUser,
    queryKey: ['getUser', { userId: itemId }],
  });
  return (
    <FormularioDeUsuario voltarPara={`configuracoes/permissoes?usuario=${itemId}`}>
      {isFetching ? (
        <Flex justify="center" py={16}>
          <Spinner color={AZUL} size="lg" />
        </Flex>
      ) : (
        <UsersForm defaultValues={isError ? undefined : (data as any)?.data} />
      )}
    </FormularioDeUsuario>
  );
}
